import type { MailResource } from '@mrrmlab/api-client';
import { normalizeSenderAddr, SENDER_MEMORY_MIN_CONFIRM } from '@mrrmlab/shared-types';

export interface CategorizeFolderResult {
  checked: number;
  categorized: number;
}

const TARGET = 50;
const PAGE_SIZE = 30;
const IMAP_TIMEOUT_MS = 30_000;
const LLM_TIMEOUT_MS = 90_000;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error(`Timeout nach ${ms / 1000}s (${label})`)), ms),
    ),
  ]);
}

export async function categorizeFolderItems(
  api: MailResource,
  suggest: (snippet: string, tags: string[]) => Promise<string[]>,
  accountId: string,
  folder: string,
  cancelRef: { current: boolean },
  onProgress?: (categorized: number, target: number) => void,
): Promise<CategorizeFolderResult> {
  const allTags = await api.listTags();
  const existingTagNames = allTags.map((t) => t.name);

  let categorized = 0;
  let checked = 0;
  let pageNum = 1;
  let hasMore = true;

  while (categorized < TARGET && hasMore && !cancelRef.current) {
    const page = await api.listMessages(accountId, folder, { page: pageNum, limit: PAGE_SIZE });
    const messages = page.messages;
    if (!messages.length) break;

    hasMore = page.page * page.limit < page.total;
    pageNum++;

    const tagsByUid = await api.listMessageTagsByUids(accountId, folder, messages.map((m) => m.uid));
    const taggedUids = new Set(
      tagsByUid.filter((row) => row.tags.length > 0).map((row) => row.uid),
    );
    const untagged = messages.filter((m) => !taggedUids.has(m.uid));

    const sendersByUid = new Map<number, string>();
    for (const env of untagged) {
      const s = normalizeSenderAddr(env.from);
      if (s) sendersByUid.set(env.uid, s);
    }
    const uniqueSenders = [...new Set(sendersByUid.values())];
    const tagBySender = new Map<string, string>();
    if (uniqueSenders.length > 0) {
      try {
        const entries = await api.lookupSenderMemory(uniqueSenders);
        for (const entry of entries) {
          const best = entry.tags
            .filter((t) => t.confirmCount >= SENDER_MEMORY_MIN_CONFIRM && t.confirmCount > t.removeCount)
            .sort((a, b) => (b.confirmCount - b.removeCount) - (a.confirmCount - a.removeCount))[0];
          if (best) tagBySender.set(entry.senderAddr, best.name);
        }
      } catch (err) {
        console.warn('[Categorize] sender-memory lookup failed:', err);
      }
    }

    for (const envelope of untagged) {
      if (categorized >= TARGET || cancelRef.current) break;
      try {
        const msg = await withTimeout(
          api.getMessage(accountId, folder, envelope.uid),
          IMAP_TIMEOUT_MS,
          'IMAP getMessage',
        );
        if (!msg.id) continue;

        const memoryTag = tagBySender.get(sendersByUid.get(envelope.uid) ?? '');
        if (memoryTag) {
          await api.assignTag(msg.id, memoryTag, 'suggested').catch(() => {});
          checked++;
          categorized++;
          onProgress?.(categorized, checked);
          continue;
        }

        const snippet = [msg.subject, msg.bodyText].filter(Boolean).join('\n').slice(0, 500);
        const suggestions = await withTimeout(
          suggest(snippet, existingTagNames),
          LLM_TIMEOUT_MS,
          'LLM suggest',
        );

        checked++;
        onProgress?.(categorized, checked);
        if (suggestions.length > 0) {
          for (const name of suggestions) {
            await api.assignTag(msg.id, name, 'suggested').catch(() => {});
          }
          categorized++;
          onProgress?.(categorized, checked);
        }
      } catch (err) {
        console.warn('[Categorize] uid', envelope.uid, 'failed:', err);
      }
    }
  }

  return { checked, categorized };
}
